import { StorableMfe, Constants } from '@storable/mfe/mfe';
import {
    MfeButtonAction,
    StoPayMFEMessageType,
    StopayPaymentMethod,
    ConfigurationProps,
} from '../checkout/models';
import { useEffect, useState, useRef } from 'react';
import { isWindowWithinHostedContext } from '../utilities/mfe-helpers';
import { getMfeHeight } from '../../utilities/helper';
import { MfeException } from '../errors/mfe-error.exception';
import { SavePaymentMethod } from '../context/mfe.context';

const MFE_TITLE = 'Payments Portal';
export const LOCAL_STORAGE_TOKEN_KEY = 'token';
const LOCAL_STORAGE_FACILITY_REPORTING_KEY = 'isFacilityLevelReportingEnabled';
const LOCAL_STORAGE_REPORTING_IDS_KEY = 'reportingFacilityIds';
const EVENT_TYPE_FACILITY_LEVEL_REPORTING = 'facilityLevelReporting';
const DEBUG_AUTH_TOKEN_RECEIVED = 'Auth token has been received';
const DEBUG_FACILITY_REPORTING_RECEIVED =
    'facilityLevelReporting event has been received';
const DEBUG_MFE_STARTED = 'MFE provider started';
const DEBUG_REQUESTED_TOKEN = 'Requested token';
const ERROR_REQUEST_TOKEN = 'Error requesting token: ';

let mfeInstance: StorableMfe | null = null;
let mfeStarted = false;
let timeOutId: any = null;
let mfeHeight: number;
let minMfeHeight: number;
const mfeWidth = 600;

export const useInitializeMFEProvider = (
    paymentMethod?: StopayPaymentMethod,
    config?: ConfigurationProps,
    setAvailableToken: React.Dispatch<
        React.SetStateAction<string | undefined>
    > = () => {},
    initialHeight: number = 400
) => {
    const [intentData, setIntentData] = useState(null);
    const [token, setToken] = useState<string>('');
    const [standAloneMode, setStandAloneMode] = useState(false);
    const [btnsClickedCount, setBtnsClickedCount] = useState({
        [MfeButtonAction.PAY]: 0,
        [MfeButtonAction.SAVE]: 0,
        [MfeButtonAction.CANCEL]: 0,
    });
    const [saveCardForFutureUsage, setSaveCardForFutureUsage] =
        useState<SavePaymentMethod>({ value: false, iterator: 0 });

    const [MFEheight, setHeight] = useState(0);
    const [isMfeEmbedAcknowledged, setIsMfeEmbedAcknowledged] = useState(false);
    const [mfe, setMfe] = useState<StorableMfe | null>(mfeInstance);
    const embedAckedRef = useRef(false);

    const { mfeHeight: height, minMfeHeight: minHeight } = getMfeHeight(
        paymentMethod,
        config
    );

    if (height) {
        mfeHeight = height;
        setTimeout(() => setHeight(mfeHeight), 1000);
    }
    if (minHeight) minMfeHeight = minHeight;

    const debounce = (paymentAction: any, timeOut: number) => {
        if (timeOutId) clearTimeout(timeOutId);
        timeOutId = setTimeout(() => paymentAction(), timeOut);
    };

    const handleAuthentication = (event: Event) => {
        const message = (event as unknown as CustomEvent).detail;
        const { token: payloadToken } = message.payload;
        console.debug(DEBUG_AUTH_TOKEN_RECEIVED);
        if (payloadToken) {
            window.localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, payloadToken);
            setToken(payloadToken);
            setAvailableToken(payloadToken);
        }
    };

    const handleEmbedAcknowledged = (event: Event) => {
        console.debug('Embed acknowledged event received', event);
        if (embedAckedRef.current) return;
        embedAckedRef.current = true;

        setIsMfeEmbedAcknowledged(true);
        mfeInstance?.removeEventListener(
            Constants.EventNames.EMBED_ACKNOWLEDGED,
            handleEmbedAcknowledged
        );
    };

    const onButtonClick = (type: MfeButtonAction) => {
        setBtnsClickedCount((prevData) => {
            return {
                ...prevData,
                [type]: prevData[type] + 1,
            };
        });
    };

    const handleAppMessages = (event: Event) => {
        const message = (event as unknown as CustomEvent).detail;
        const { type, data } = message.payload;

        if (type === EVENT_TYPE_FACILITY_LEVEL_REPORTING) {
            let isFacilityLevelReportingEnabled =
                data.isFacilityLevelReportingEnabled;
            const reportingFacilityIds = data.reportingFacilityIds || null;

            isFacilityLevelReportingEnabled =
                isFacilityLevelReportingEnabled &&
                Array.isArray(reportingFacilityIds) &&
                reportingFacilityIds.length > 0;

            window.localStorage.setItem(
                LOCAL_STORAGE_FACILITY_REPORTING_KEY,
                JSON.stringify(isFacilityLevelReportingEnabled)
            );
            window.localStorage.setItem(
                LOCAL_STORAGE_REPORTING_IDS_KEY,
                JSON.stringify(reportingFacilityIds)
            );

            console.debug(DEBUG_FACILITY_REPORTING_RECEIVED);
        } else if (type === StoPayMFEMessageType.IntentUpdate) {
            setIntentData(data);
        } else if (type === StoPayMFEMessageType.AuthToken) {
            handleAuthentication(event);
        } else if (type === StoPayMFEMessageType.ProcessPayment) {
            debounce(() => onButtonClick(MfeButtonAction.PAY), 200);
        } else if (type === StoPayMFEMessageType.SavePayment) {
            debounce(() => onButtonClick(MfeButtonAction.SAVE), 200);
        } else if (type === StoPayMFEMessageType.CancelPayment) {
            debounce(() => onButtonClick(MfeButtonAction.CANCEL), 200);
        } else if (type === StoPayMFEMessageType.SavePaymentMethod) {
            setSaveCardForFutureUsage((prevData: SavePaymentMethod) => {
                return {
                    ...prevData,
                    value: data.value,
                    iterator: (prevData.iterator += 1),
                };
            });
        } else {
            throw new MfeException({
                message: 'Invalid MFE event recieved',
                statusCode: 400,
            });
        }
    };

    useEffect(() => {
        (async () => {
            if (!isWindowWithinHostedContext()) return setStandAloneMode(true);
            console.debug(
                'Application has detected that it is within an iframe'
            );

            if (!mfeInstance) {
                mfeInstance = new StorableMfe(MFE_TITLE);
            } else {
                embedAckedRef.current = true;
                setIsMfeEmbedAcknowledged(true);
            }

            setMfe(mfeInstance);

            mfeInstance.addEventListener(
                Constants.EventNames.EMBED_ACKNOWLEDGED,
                handleEmbedAcknowledged
            );

            mfeInstance.addEventListener(
                Constants.EventNames.APPLY_AUTH_TOKEN,
                handleAuthentication
            );

            mfeInstance.addEventListener(
                Constants.EventNames.APP_MESSAGE,
                handleAppMessages
            );

            if (!mfeStarted) {
                mfeStarted = true;
                try {
                    await mfeInstance.start({
                        title: MFE_TITLE,
                        initialSize: {
                            width: mfeWidth,
                            height: mfeHeight || initialHeight,
                        },
                        minimumSize: {
                            width: mfeWidth,
                            height: minMfeHeight || initialHeight,
                        },
                        fullscreen: false,
                        allowResize: true,
                    });
                    console.debug(DEBUG_MFE_STARTED);
                } catch (error) {
                    mfeStarted = false;
                    console.error('Error starting MFE: ', error);
                    return;
                }

                try {
                    mfeInstance.requestAuthToken();
                    console.debug(DEBUG_REQUESTED_TOKEN);
                } catch (error) {
                    console.error(ERROR_REQUEST_TOKEN, error);
                }
            }
        })();
        return () => {
            mfeInstance?.removeEventListener(
                Constants.EventNames.APPLY_AUTH_TOKEN,
                handleAuthentication
            );
            mfeInstance?.removeEventListener(
                Constants.EventNames.APP_MESSAGE,
                handleAppMessages
            );
            mfeInstance?.removeEventListener(
                Constants.EventNames.EMBED_ACKNOWLEDGED,
                handleEmbedAcknowledged
            );
            embedAckedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (MFEheight) {
            mfeInstance?.updateDisplay({
                title: MFE_TITLE,
                initialSize: {
                    width: mfeWidth,
                    height: mfeHeight || initialHeight,
                },
                minimumSize: {
                    width: mfeWidth,
                    height: minMfeHeight || initialHeight,
                },
                fullscreen: false,
                allowResize: true,
            });
        }
    }, [MFEheight]);

    return {
        mfe,
        mfeHostToken: token,
        intentUpdateData: intentData,
        standAloneMode,
        btnsClickedCount,
        saveCardForFutureUsage,
        isMfeEmbedAcknowledged,
    };
};

export const getMfeInstance = (): StorableMfe | null => mfeInstance;
